import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { EASE, Reveal, SectionHead } from "./shared";

const PHOTOS = [
  { src: "/assets/lago-nebbia.jpg", caption: "L'isola nella nebbia" },
  { src: "/assets/lago-rami-verdi.jpg", caption: "Tra i rami, l'isola" },
  { src: "/assets/lago-panorama.jpg", caption: "Il lago d'inverno" },
  { src: "/assets/lago-scogli.jpg", caption: "L'acqua limpida del lago con sullo sfondo l'Isola e le montagne" },
  { src: "/assets/lago-riva.jpg", caption: "Vista sul lago" },
  { src: "/assets/lago-ghiaia.jpg", caption: "La riva di ghiaia" },
  { src: "/assets/lago-acqua-chiara.jpg", caption: "Acqua chiara" },
  { src: "/assets/lago-rami.jpg", caption: "Acque profonde" },
  { src: "/assets/lago-spiaggetta.jpg", caption: "La spiaggetta dove Marco incontrava il fantasma di Elisabetta" },
  { src: "/assets/lago-isola-nebbia.jpg", caption: "San Giulio, velata" },
  { src: "/assets/chiesetta-bosco.jpg", caption: "La chiesetta di San Giulio, costa ovest" },
  { src: "/assets/isola-1.jpg", caption: "Il pozzo dell'isola" },
  { src: "/assets/isola-2.jpg", caption: "I palazzi dell'isola sull'acqua" },
  { src: "/assets/isola-3.jpg", caption: "Vicolo dell'isola" },
  { src: "/assets/isola-4.jpg", caption: "Il molo dell'isola" },
  { src: "/assets/orta-piazza.jpg", caption: "L'imbarco nella piazza di Orta per raggiungere l'isola" },
  { src: "/assets/orta-imbarcadero.jpg", caption: "La piazza di Orta" },
  { src: "/assets/chiesetta-san-giulio.jpg", caption: "La chiesetta e la sua sorgente" },
  { src: "/assets/lagna-lavatoio.jpg", caption: "Il lavatoio di Lagna" },
  { src: "/assets/spiaggia-prarolo.jpg", caption: "La spiaggia di Prarolo" },
  { src: "/assets/boec-orchera.jpg", caption: "Il Böc del'Orchera, il Buco del Drago" },
];

const EVENT_PHOTOS = [
  {
    src: "/assets/evento-prima-1.jpg",
    caption: "L'intervista sul palco, accanto alla locandina del romanzo",
    testid: "gallery-event-1",
  },
  {
    src: "/assets/evento-prima-2.jpg",
    caption: "La presentazione davanti a una sala piena",
    testid: "gallery-event-2",
  },
  {
    src: "/assets/evento-prima-3.jpg",
    caption: "Le prime lettrici con il romanzo e il manifesto della saga",
    testid: "gallery-event-3",
  },
];

export default function Galleria() {
  const [open, setOpen] = useState(null);

  const close = useCallback(() => setOpen(null), []);
  const step = useCallback(
    (dir) => setOpen((i) => (i === null ? null : (i + dir + PHOTOS.length) % PHOTOS.length)),
    []
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, step]);

  return (
    <section id="galleria" className="relative bg-lake-deep py-20 lg:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          kicker="La Galleria"
          title="Il Lago di Patrizia"
          sub="Il lago visto dai miei occhi: fotografie scattate negli anni sulle rive di Orta, tra nebbie d'inverno e acque d'estate. Ogni luogo del romanzo è passato prima da qui."
        />

        <div className="mt-14 columns-2 gap-4 md:columns-3" data-testid="gallery-grid">
          {PHOTOS.map((p, i) => (
            <Reveal key={p.src} delay={(i % 3) * 0.08} className="mb-4 break-inside-avoid">
              <motion.button
                data-testid={`gallery-photo-${i}`}
                onClick={() => setOpen(i)}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 180, damping: 18 }}
                className="group relative block w-full overflow-hidden rounded-lg border border-gold/20 text-left"
                aria-label={`Apri foto: ${p.caption}`}
              >
                <img
                  src={p.src}
                  alt={`${p.caption} — Lago d'Orta, fotografia di Patrizia Franzina`}
                  className="block w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-lake-deep/80 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <p className="absolute bottom-0 left-0 right-0 translate-y-2 p-4 font-serif text-sm italic text-parchment-ivory opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {p.caption}
                </p>
              </motion.button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <p className="mt-8 text-center text-xs uppercase tracking-[0.3em] text-parchment-ivory/45">
            Tutte le fotografie sono di Patrizia Franzina
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-20 border-t border-gold/15 pt-14" data-testid="gallery-events-block">
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Dagli eventi</p>
            <h3 className="mt-3 font-serif text-2xl text-parchment-ivory sm:text-3xl">
              La prima presentazione — 31 ottobre 2026
            </h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-parchment-ivory/60">
              Teatro degli Scalpellini, San Maurizio d'Opaglio: la prima volta del Segreto davanti
              al pubblico, alla vigilia dell'uscita in libreria.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {EVENT_PHOTOS.map((p) => (
                <figure
                  key={p.src}
                  data-testid={p.testid}
                  className="group relative h-full overflow-hidden rounded-lg border border-gold/20"
                >
                  <img
                    src={p.src}
                    alt={`${p.caption} — prima presentazione de Il Segreto del Lago, 31 ottobre 2026`}
                    className="block h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-lake-deep/90 to-transparent p-4 pt-10 font-serif text-sm italic text-parchment-ivory">
                    {p.caption}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-lake-deep/95 p-4 backdrop-blur-md"
            onClick={close}
            data-testid="gallery-lightbox"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={close}
              aria-label="Chiudi galleria"
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 text-parchment-ivory transition-colors hover:bg-gold/15"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              data-testid="gallery-prev"
              onClick={(e) => {
                e.stopPropagation();
                step(-1);
              }}
              aria-label="Foto precedente"
              className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gold/40 text-parchment-ivory transition-colors hover:bg-gold/15 sm:left-6"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="gallery-next"
              onClick={(e) => {
                e.stopPropagation();
                step(1);
              }}
              aria-label="Foto successiva"
              className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gold/40 text-parchment-ivory transition-colors hover:bg-gold/15 sm:right-6"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <motion.figure
              key={open}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: EASE }}
              className="max-w-4xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={PHOTOS[open].src}
                alt={`${PHOTOS[open].caption} — Lago d'Orta, fotografia di Patrizia Franzina`}
                className="max-h-[78vh] w-auto max-w-full rounded-lg border border-gold/30 shadow-2xl"
              />
              <figcaption className="mt-4 flex items-baseline justify-between gap-4">
                <span className="font-serif text-lg italic text-parchment-ivory" data-testid="gallery-lightbox-caption">
                  {PHOTOS[open].caption}
                </span>
                <span className="shrink-0 text-xs uppercase tracking-[0.25em] text-parchment-ivory/50">
                  Foto di Patrizia Franzina · {open + 1}/{PHOTOS.length}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
